<template>
  <component
    :is="embedded ? 'div' : 'aside'"
    ref="panelEl"
    class="map-list-docked-panel"
    :class="{ 'map-list-docked-panel--embedded': embedded }"
    :style="embedded ? undefined : { width: panelWidth + 'px' }"
  >
    <div
      v-if="!embedded"
      class="map-list-panel-resize"
      @mousedown.prevent="startResize"
    >
      <div class="map-list-panel-resize-grip" />
    </div>

    <div
      v-if="!embedded"
      class="map-list-panel-header"
      :style="((mapViewMode === 'aoi' && hasAreas) || (mapViewMode === 'grid' && selectedHexCells.size > 0) || mapViewMode === 'swath') ? { borderBottom: 'none', paddingBottom: '0.15rem' } : {}"
    >
      <span class="map-list-panel-title">
        <FontAwesomeIcon icon="fas fa-list" />
        <strong>{{ $t('list.title') }}</strong>
        <span class="map-list-panel-count">({{ displayCount }})</span>
      </span>

      <div v-if="mapViewMode === 'aoi' && areas.length > 0" class="timeline-aoi-selector">
        <button class="timeline-aoi-selector-btn" @click="aoiDropdownOpen = !aoiDropdownOpen">
          <FontAwesomeIcon icon="fas fa-map" />
          <span>{{ aoiSelectorLabel }}</span>
          <FontAwesomeIcon :icon="aoiDropdownOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="timeline-aoi-chevron" />
        </button>
        <div v-if="aoiDropdownOpen" class="timeline-aoi-dropdown">
          <label
            v-for="area in areas"
            :key="area.id"
            class="timeline-aoi-dropdown-item"
          >
            <input
              type="checkbox"
              :checked="area.visible"
              @change="setAreaVisibility(area.id, !area.visible)"
            />
            <span>{{ area.name }}</span>
          </label>
        </div>
      </div>

      <div class="map-list-panel-actions">
        <div class="cloud-filter-inline" v-tooltip.left="$t('list.maxCloudCover', { value: cloudCoverThreshold })">
          <FontAwesomeIcon icon="fas fa-cloud" class="cloud-filter-inline-icon" />
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            class="cloud-filter-inline-slider"
            :value="cloudCoverThreshold"
            @input="onCloudCoverSliderInput(($event.target as HTMLInputElement).value)"
          />
          <label class="cloud-filter-inline-value">
            <span class="cloud-filter-inline-value-prefix">≤</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              inputmode="numeric"
              class="cloud-filter-inline-value-input"
              :value="cloudCoverInputValue"
              :aria-label="$t('list.maxCloudCover', { value: cloudCoverThreshold })"
              @input="onCloudCoverValueInput(($event.target as HTMLInputElement).value)"
              @blur="commitCloudCoverValueInput"
              @keydown.enter.prevent="commitCloudCoverValueInput"
            />
            <span class="cloud-filter-inline-value-suffix">%</span>
          </label>
        </div>
        <Button
          v-tooltip.left="$t('list.close')"
          icon="fas fa-xmark"
          severity="contrast"
          text
          size="small"
          @click="$emit('close')"
        />
      </div>
    </div>

    <div
      v-if="!embedded && ((mapViewMode === 'aoi' && hasAreas) || (mapViewMode === 'grid' && selectedHexCells.size > 0) || mapViewMode === 'swath')"
      class="w-full flex items-center gap-1.5 cursor-default pl-[0.6rem] pb-2 text-[10.5px] font-normal text-slate-500 dark:text-slate-400 opacity-90 tracking-wide"
      style="border-bottom: 1px solid rgba(255, 255, 255, 0.08);"
      v-tooltip.bottom="'Geostationary satellites monitor this area continuously, capturing data at regular intervals.'"
    >
      <FontAwesomeIcon icon="fas fa-satellite-dish" class="text-blue-500" />
      <span>Continuous Geostationary Coverage</span>
    </div>

    <div ref="listContainer" class="map-list-panel-body" :class="{ 'map-list-panel-body--embedded': embedded }">
      <div v-if="loading" class="map-list-panel-placeholder">{{ $t('list.loading') }}</div>
      <div v-else-if="mapViewMode === 'aoi' && !hasAreas" class="map-list-panel-placeholder">{{ $t('list.uploadAoiToView') }}</div>
      <div v-else-if="mapViewMode === 'grid' && selectedHexCells.size === 0" class="map-list-panel-placeholder">{{ $t('list.selectGridCell') }}</div>
      <div v-else-if="visiblePassCount === -1" class="map-list-panel-placeholder">{{ $t('list.zoomIn') }}</div>
      <div v-else-if="!displayPasses.length" class="map-list-panel-placeholder">{{ $t('list.noOverpasses') }}</div>

      <table v-else class="map-list-table">
        <thead>
          <tr>
            <th>{{ $t('list.satellite') }}</th>
            <th v-if="showDirection">{{ $t('list.direction') }}</th>
            <th>{{ $t('list.start') }}</th>
            <th>{{ $t('list.end') }}</th>
            <th>{{ $t('list.sensor') }}</th>
            <th>{{ $t('list.resolution') }}</th>
            <th>{{ $t('list.light') }}</th>
            <th>{{ $t('list.cloud') }}</th>
            <th>{{ $t('list.latency') }}</th>
            <th>{{ $t('list.delivery') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="pass in displayPasses"
            :key="pass.id"
            :ref="(el) => setPassRowRef(pass.id, el as HTMLElement | null)"
            :class="{ 'is-selected': isDisplayPassSelected(pass), 'is-hovered': isDisplayPassHovered(pass) }"
            @click="selectPass(pass)"
            @mouseenter="onRowEnter(pass)"
            @mouseleave="onRowLeave(pass)"
          >
            <td class="map-list-sat">
              <span class="map-list-pass-dot" :style="{ backgroundColor: getPassTimingColor(pass) }" />
              <span>{{ pass.name }}</span>
            </td>
            <td v-if="showDirection">
              <span
                v-if="pass.direction"
                class="map-list-badge"
                :class="pass.direction === 'ASCENDING' ? 'badge-direction-ascending' : 'badge-direction-descending'"
              >{{ pass.direction === 'ASCENDING' ? $t('popup.ascending') : $t('popup.descending') }}</span>
            </td>
            <td>{{ formatDateTime(pass.start_time) }}</td>
            <td>{{ formatDateTime(pass.end_time) }}</td>
            <td>
              <span v-if="pass.sensor_type" class="map-list-badge badge-blue">{{ pass.sensor_type }}</span>
            </td>
            <td>
              <span v-if="pass.spatial_res_m != null">{{ pass.spatial_res_m }}m</span>
            </td>
            <td>
              <span
                v-if="pass.is_daytime != null"
                class="map-list-badge"
                :class="pass.is_daytime ? 'badge-yellow' : 'badge-purple'"
              >
                {{ pass.is_daytime ? $t('list.day') : $t('list.night') }}
              </span>
            </td>
            <td>
              <span
                v-if="pass.cloud_cover_pct != null"
                class="map-list-badge"
                :class="
                  pass.cloud_cover_pct >= 75 ? 'badge-cloud-high' :
                  pass.cloud_cover_pct >= 40 ? 'badge-cloud-mid' :
                  'badge-cloud-low'
                "
              >{{ pass.cloud_cover_pct }}%</span>
              <span v-else class="map-list-badge badge-cloud-unavailable">{{ $t('popup.notCalculated') }}</span>
            </td>
            <td>
              <span v-if="getPassLatency(pass) != null" class="map-list-badge badge-latency">~{{ Math.round(getPassLatency(pass)!) }} min</span>
            </td>
            <td>
              <span v-if="getPassDelivery(pass)">{{ formatDateTime(getPassDelivery(pass)!) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Button from 'primevue/button';
import { useFilterStore, type VisiblePass } from '@/composables/useFilterStore';
import { useMonitoredAreasStore } from '@/composables/useMonitoredAreasStore';
import { parseUtcEpochMs, useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { getTimingColorForHours, NO_UPCOMING_HOURS } from '@/composables/useTimingColorScale';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';
import {
  collapseOroraConstellationPasses,
  getPrimaryPassId,
  groupedPassContainsRawId,
  type GroupedVisiblePass,
} from '@/composables/useConstellationGrouping';

withDefaults(
  defineProps<{
    embedded?: boolean;
  }>(),
  {
    embedded: false,
  },
);

defineEmits<{
  (e: 'close'): void;
}>();

const {
  visiblePasses: swathVisiblePasses,
  visiblePassCount: swathVisiblePassCount,
  loading,
  selectedPassId,
  hoveredPassId,
  setSelectedPass,
  setHoveredPass,
  mapViewMode,
  timeRange,
  ororaConstellationExpanded,
  hexTimingSelectedPasses,
  selectedHexCells,
  cloudCoverThreshold,
  passWithinCloudThreshold,
} = useFilterStore();

const { aoiPasses, hasAreas, areas, setAreaVisibility } = useMonitoredAreasStore();
const { formatDateTime } = useDateTimeSettings();

const panelEl = ref<HTMLElement | null>(null);
const panelWidth = ref(360);
const MIN_WIDTH = 260;
const MAX_WIDTH_FALLBACK = 520;
const cloudCoverInputValue = ref(String(cloudCoverThreshold.value));

const visiblePasses = computed<VisiblePass[]>(() => {
  let source: VisiblePass[];
  if (mapViewMode.value === 'aoi') source = aoiPasses.value;
  else if (mapViewMode.value === 'grid') source = hexTimingSelectedPasses.value;
  else source = swathVisiblePasses.value;
  return source.filter(passWithinCloudThreshold);
});

const displayPasses = computed<GroupedVisiblePass[]>(() =>
  collapseOroraConstellationPasses(visiblePasses.value, ororaConstellationExpanded.value, {
    timeBucketReferenceMs: timeRange.value[0],
  }),
);

const showDirection = computed(() => displayPasses.value.some((pass) => pass.direction != null));

const visiblePassCount = computed<number | null>(() => {
  if (mapViewMode.value === 'aoi') return aoiPasses.value.length;
  if (mapViewMode.value === 'grid') {
    if (selectedHexCells.value.size === 0) return 0;
    return hexTimingSelectedPasses.value.length;
  }
  return swathVisiblePassCount.value;
});

const displayCount = computed(() => {
  if (visiblePassCount.value === -1) return '...';
  return displayPasses.value.length;
});

const aoiDropdownOpen = ref(false);
const aoiSelectorLabel = computed(() => {
  const visible = areas.value.filter((a) => a.visible);
  if (areas.value.length === 1) return visible.length === 1 ? areas.value[0].name : 'No AOIs';
  if (areas.value.length > 1 && visible.length === areas.value.length) return 'All AOIs';
  if (visible.length === 0) return 'No AOIs';
  if (visible.length === 1) return visible[0].name;
  return `${visible.length} AOIs`;
});

function onClickOutsideAoiDropdown(e: MouseEvent) {
  const target = e.target as HTMLElement;
  if (!target.closest('.timeline-aoi-selector')) aoiDropdownOpen.value = false;
}
watch(aoiDropdownOpen, (open) => {
  if (open) document.addEventListener('click', onClickOutsideAoiDropdown, { capture: true });
  else document.removeEventListener('click', onClickOutsideAoiDropdown, { capture: true });
});
watch(mapViewMode, (mode) => {
  if (mode !== 'aoi') aoiDropdownOpen.value = false;
});
onBeforeUnmount(() => document.removeEventListener('click', onClickOutsideAoiDropdown, { capture: true }));

function clampCloudCoverThreshold(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function onCloudCoverSliderInput(rawValue: string): void {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return;
  cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
}

function onCloudCoverValueInput(rawValue: string): void {
  cloudCoverInputValue.value = rawValue;
  if (rawValue.trim() === '') return;
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return;
  cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
}

function commitCloudCoverValueInput(): void {
  const parsed = Number(cloudCoverInputValue.value);
  if (Number.isFinite(parsed)) {
    cloudCoverThreshold.value = clampCloudCoverThreshold(parsed);
  }
  cloudCoverInputValue.value = String(cloudCoverThreshold.value);
}

watch(cloudCoverThreshold, (value) => {
  if (cloudCoverInputValue.value !== String(value)) {
    cloudCoverInputValue.value = String(value);
  }
});

function maxPanelWidth(): number {
  const parentW = panelEl.value?.parentElement?.clientWidth ?? window.innerWidth;
  return Math.min(MAX_WIDTH_FALLBACK, Math.max(MIN_WIDTH, Math.floor(parentW * 0.36)));
}

function startResize(e: MouseEvent) {
  const startX = e.clientX;
  const startW = panelWidth.value;

  const onMove = (ev: MouseEvent) => {
    const delta = ev.clientX - startX;
    const next = startW + delta;
    panelWidth.value = Math.max(MIN_WIDTH, Math.min(maxPanelWidth(), next));
  };

  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
}

const listContainer = ref<HTMLDivElement | null>(null);
const passRowRefs = new Map<string, HTMLElement>();
const suppressAutoScrollFromList = ref(false);

function setPassRowRef(id: string, el: HTMLElement | null) {
  if (el) passRowRefs.set(id, el);
  else passRowRefs.delete(id);
}

function selectPass(pass: GroupedVisiblePass) {
  const targetId = getPrimaryPassId(pass);
  suppressAutoScrollFromList.value = true;
  if (groupedPassContainsRawId(pass, selectedPassId.value)) {
    setSelectedPass(null);
  } else {
    setSelectedPass(targetId);
  }
  // Safety reset in case the selection value didn't actually change.
  queueMicrotask(() => { suppressAutoScrollFromList.value = false; });
}

watch(selectedPassId, (id) => {
  if (!id) return;
  if (suppressAutoScrollFromList.value) {
    suppressAutoScrollFromList.value = false;
    return;
  }
  if (mapViewMode.value !== 'swath') return;
  const displayId = displayPasses.value.find((pass) => groupedPassContainsRawId(pass, id))?.id ?? id;
  nextTick(() => scrollToPass(displayId));
});

function scrollToPass(id: string) {
  const row = passRowRefs.get(id);
  const container = listContainer.value;
  if (!row || !container) return;
  const thead = container.querySelector('thead');
  const theadH = thead?.getBoundingClientRect().height ?? 0;
  const containerRect = container.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();
  const desiredScrollTop = container.scrollTop + (rowRect.top - containerRect.top) - theadH;
  container.scrollTo({ top: Math.max(0, desiredScrollTop), behavior: 'smooth' });
}

function onRowEnter(pass: GroupedVisiblePass) {
  setHoveredPass(getPrimaryPassId(pass), pass.sourcePassIds);
}

function onRowLeave(pass: GroupedVisiblePass) {
  if (groupedPassContainsRawId(pass, hoveredPassId.value)) setHoveredPass(null);
}

function isDisplayPassSelected(pass: GroupedVisiblePass): boolean {
  return groupedPassContainsRawId(pass, selectedPassId.value);
}

function isDisplayPassHovered(pass: GroupedVisiblePass): boolean {
  return groupedPassContainsRawId(pass, hoveredPassId.value);
}

function getPassTimingHours(pass: GroupedVisiblePass): number {
  const startMs = parseUtcEpochMs(pass.start_time);
  if (!Number.isFinite(startMs)) return NO_UPCOMING_HOURS;
  return Math.max(0, (startMs - timeRange.value[0]) / 3_600_000);
}

function getPassTimingColor(pass: GroupedVisiblePass): string {
  return getTimingColorForHours(getPassTimingHours(pass), 0.95, 0.95);
}

function getPassLatency(pass: GroupedVisiblePass): number | null {
  return getExpectedLatencyMinutesForPass(pass);
}

function getPassDelivery(pass: GroupedVisiblePass): string | null {
  return computeExpectedDeliveryForPass(pass);
}
</script>

<style scoped lang="scss">
.map-list-docked-panel {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: var(--p-navbar-background, #0C232B);
  color: var(--p-navbar-color, #ffffffcc);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  min-height: 0;
}

.map-list-docked-panel--embedded {
  width: 100%;
  background: transparent;
  border-right: none;
}

.map-list-panel-resize {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 10px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.map-list-panel-resize-grip {
  width: 3px;
  height: 46px;
  border-radius: 2px;
  background: rgba(3, 222, 204, 0.55);
}

.map-list-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: var(--mk-bottom-panel-content-header-height, 44px);
  box-sizing: border-box;
  padding: 0 0.9rem 0 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.map-list-panel-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.05rem;
}

.map-list-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.84rem;
}

.map-list-panel-count {
  opacity: 0.68;
  font-weight: 400;
}

.map-list-panel-body {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.map-list-panel-body--embedded {
  background: transparent;
}

.map-list-panel-placeholder {
  padding: 1rem;
  opacity: 0.65;
  font-size: 0.8rem;
}

.map-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.76rem;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    height: var(--mk-bottom-panel-content-header-height, 44px);
    box-sizing: border-box;
    padding: 0 0.6rem;
    vertical-align: middle;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--p-text-muted-color, #8DA0A7);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: var(--p-navbar-background, #0C232B);
  }

  td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
    white-space: nowrap;
  }

  tr {
    cursor: pointer;
  }

  tr:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  tr.is-selected {
    background: rgba(3, 222, 204, 0.15);

    td {
      color: #03DECC;
    }
  }

  tr.is-hovered:not(.is-selected) {
    background: rgba(3, 222, 204, 0.1);
  }
}

.map-list-sat {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  font-weight: 600;
}

.map-list-pass-dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}

.map-list-badge {
  display: inline-block;
  padding: 0.08rem 0.35rem;
  border-radius: 3px;
  font-size: 0.66rem;
  text-transform: capitalize;
}

.badge-blue { background: rgba(59, 130, 246, 0.2); }
.badge-purple { background: rgba(139, 92, 246, 0.2); }
.badge-yellow { background: rgba(234, 179, 8, 0.2); }
.badge-direction-ascending { background: rgba(34, 197, 94, 0.2); }
.badge-direction-descending { background: rgba(249, 115, 22, 0.2); }
.badge-cloud-low  { background: rgba(34, 197, 94, 0.2); }
.badge-cloud-mid  { background: rgba(234, 179, 8, 0.2); }
.badge-cloud-high { background: rgba(148, 163, 184, 0.25); }
.badge-cloud-unavailable { background: rgba(148, 163, 184, 0.14); }
.badge-latency    { background: rgba(3, 222, 204, 0.15); }

:root:not(.color-scheme-dark) {
  .map-list-docked-panel {
    background: #ffffff;
    color: #1e293b;
    border-right-color: rgba(15, 23, 42, 0.12);
  }

  .map-list-panel-header,
  .map-list-table th {
    border-bottom-color: rgba(15, 23, 42, 0.12);
    background: #ffffff;
  }

  .map-list-table td {
    border-bottom-color: rgba(15, 23, 42, 0.06);
  }

  .map-list-table tr:hover {
    background: rgba(15, 23, 42, 0.04);
  }

  .map-list-table tr.is-hovered:not(.is-selected) {
    background: rgba(15, 23, 42, 0.08);
  }

  .map-list-table tr.is-selected td {
    color: #0f172a;
  }
}

:root:not(.color-scheme-dark) .map-list-docked-panel--embedded {
  background: transparent;
}
</style>
